import React from "react";
import Icon from "../Icon";
import Modal from "../Modal";
import Panel from "../Panel";
import { freeCardsRemainingToday } from "./todaysPlanService";
import { localKey, readLocal, writeLocal } from "./todaysPlanStorage";

// Custom collections (no type) are never complete: users keep adding sentences to them.
export function isCollectionCompleted({ numPlaying, numSentences, type }) {
  return !!type && numSentences > 0 && numPlaying >= numSentences;
}

function CollectionOptionsModal({ collection, onHidden, onReset, onUnpin }) {
  const modalRef = React.useRef(null);
  const choose = (action) => {
    modalRef.current.hide();
    action(collection);
  };

  return (
    <Modal onHidden={onHidden} ref={modalRef} size="small" title="Collection options">
      <div className="tw-flex tw-flex-col tw-gap-2">
        <button className="btn btn-default btn-block text-left" onClick={() => choose(onUnpin)}>
          <Icon name="pushpin" /> Unpin from Dashboard
        </button>
        <button className="btn btn-default btn-block text-left" onClick={() => choose(onReset)}>
          <Icon name="repeat" /> Reset {collection.name}
        </button>
      </div>
    </Modal>
  );
}

function CollapsibleCollectionRow({ collection, isExpanded, onOpenOptions, onToggleExpanded, renderBody, renderProgress }) {
  const { id } = collection;

  return (
    <Panel attrs={{ "data-collection-name": collection.name, "data-testid": `collection-row-${id}` }}>
      <div className="tw-flex tw-items-center tw-gap-3">
        <div className="tw-text-5xl tw-font-bold tw-flex-1">{collection.name}</div>
        <button aria-label="Collection options" className="btn btn-default" data-testid={`collection-options-button-${id}`} onClick={onOpenOptions} style={{ minHeight: 48, minWidth: 48 }}>
          <Icon name="ellipsis-v" type="fa" />
        </button>
        <button aria-label={isExpanded ? "Collapse" : "Expand"} className="btn btn-default" data-testid={`expand-chevron-${id}`} onClick={onToggleExpanded} style={{ minHeight: 48, minWidth: 48 }}>
          <Icon name={isExpanded ? "chevron-up" : "chevron-down"} />
        </button>
      </div>
      {isExpanded ? (
        <div className="tw-mt-5" data-testid={`collection-detail-${id}`}>{renderBody(collection)}</div>
      ) : (
        <div className="tw-mt-5" data-testid={`collection-progress-${id}`}>{renderProgress(collection)}</div>
      )}
    </Panel>
  );
}

export default function MyCollections({ collections, isPro, onReset, onUnpin, planStatus, renderBody, renderProgress, sortCollections, username }) {
  const expandedKey = localKey("CollectionExpanded", username);
  const [expandedById, setExpandedById] = React.useState(() => readLocal(expandedKey) || {});
  const [isCompletedVisible, setIsCompletedVisible] = React.useState(false);
  const [optionsCollection, setOptionsCollection] = React.useState(null);
  const hasAutoExpandedRef = React.useRef(false);

  const activeCollections = sortCollections(collections.filter((c) => !isCollectionCompleted(c)));
  const completedCollections = sortCollections(collections.filter(isCollectionCompleted));

  const setExpanded = (collectionId, isExpanded) => {
    const next = { ...expandedById, [collectionId]: isExpanded };
    writeLocal(expandedKey, next);
    setExpandedById(next);
  };

  React.useEffect(() => {
    if(hasAutoExpandedRef.current || isPro || !planStatus || !planStatus.isComplete || planStatus.totalItems === 0) return;
    if(freeCardsRemainingToday(collections) <= 0 || !activeCollections.length) return;
    hasAutoExpandedRef.current = true;
    setExpanded(String(activeCollections[0].id), true);
  }, [isPro, planStatus, collections]);

  const renderRow = (collection) => (
    <CollapsibleCollectionRow
      collection={collection}
      isExpanded={!!expandedById[String(collection.id)]}
      key={collection.id}
      onOpenOptions={() => setOptionsCollection(collection)}
      onToggleExpanded={() => setExpanded(String(collection.id), !expandedById[String(collection.id)])}
      renderBody={renderBody}
      renderProgress={renderProgress}
    />
  );

  return (
    <>
      <div data-testid="collections-active-section">{activeCollections.map(renderRow)}</div>
      {completedCollections.length > 0 && (
        <div data-testid="collections-completed-section">
          <button
            className="btn btn-link btn-block tw-flex tw-items-center text-secondary"
            data-testid="collections-completed-toggle"
            onClick={() => setIsCompletedVisible(!isCompletedVisible)}
            style={{ paddingLeft: 0, paddingRight: 0, textDecoration: "none" }}
          >
            <span className="tw-flex-1 text-left">Completed (reviews only)</span>
            <Icon name={isCompletedVisible ? "chevron-up" : "chevron-down"} />
          </button>
          {isCompletedVisible && completedCollections.map(renderRow)}
        </div>
      )}
      {optionsCollection && (
        <CollectionOptionsModal
          collection={optionsCollection}
          onHidden={() => setOptionsCollection(null)}
          onReset={onReset}
          onUnpin={onUnpin}
        />
      )}
    </>
  );
}
